/*
 * Copyright (c) 2024, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import React, { Suspense, useState, useEffect, useRef, SetStateAction, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CopyTextField from "../../../../elements/CopyTextField";
import { PayloadEditor } from "./PayloadEditor";
import { PlayCircleFilledOutlined, Send } from "@material-ui/icons";
import OutputConsole from "./OutputConsole";
import useStyles from "./styles";
import LogLine, { CollectedLogLineWithKey } from "./LogLine";
import { TabPanel } from '../../../shared/Tab/TabPanel';

export const OAUTH2_KEY_HEADER_NAME = "choreo-oauth2-token";
  
interface TopicViewerProps {
  token: string;
  apiEndpoint: string;
  topic: string;
  publish: boolean;
  subscribe: boolean;
  parameters?: Record<string, string>;
  payload?: string;
}

enum ButtonText {
    CONNECT = 'Connect',
    CONNECTING = 'Connecting',
    DISCONNECT = 'Disconnect',
    DISCONNECTING = "Disconnecting"
}

const TopicViewer = (props: TopicViewerProps) => {
  const { token, apiEndpoint, topic,  publish, subscribe, parameters, payload } = props;
  const classes = useStyles();
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [messages, setMessages] = useState<Record<string, any>[]>([]);
  const [input, setInput] = useState(payload || "");
  const [endpoint, setEndpoint] = useState<string>(apiEndpoint);
  const [pathParams, setPathParams] = useState<{ [key: string]: string }>({});
  const [connect, setConnect] = useState(false);
  const [connectButtonText, setConnectButtonText] = useState(ButtonText.CONNECT);
  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = (event: any, newValue: SetStateAction<number>) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (topic !== '/*') {
      const formattedTopic = topic.startsWith('/') ? topic : `/${topic}`;
      setEndpoint(`${apiEndpoint}${formattedTopic}`);
    }
    if (parameters) {
      setPathParams(
        Object.keys(parameters).reduce((acc, key) => {
          acc[key] = '';
          return acc;
        }, {} as { [key: string]: string })
      );
    }
  }, [apiEndpoint, topic, parameters]); // Added parameters to dependency array

  const sendMessage = () => {
    if (socket && input && socket.readyState === WebSocket.OPEN) {
      socket.send(input);
      setMessages((prev) => [
        ...prev,
        {
          message: `Sent: ${input}`,
          timestamp: new Date().toString(),
          randomKey: messages.length,
        },
      ]);
      setInput("");
    }
  };

  const connectWebsocket = () => {
    setConnectButtonText(ButtonText.CONNECTING);
    let updatedEndpoint = endpoint;

    for (const key in pathParams) {
      updatedEndpoint = updatedEndpoint.replace(`{${key}}`, pathParams[key]);
    }

    const ws = new WebSocket(updatedEndpoint, [
      OAUTH2_KEY_HEADER_NAME,
      token,
    ]);
    setSocket(ws);

    ws.onopen = () => {
      setConnect(true);
      setMessages((prev) => [
        ...prev,
        {
          message: `Connected to ${updatedEndpoint}`,
          timestamp: new Date().toString(),
          randomKey: messages.length,
        },
      ]);
      setConnectButtonText(ButtonText.DISCONNECT);
    };

    ws.onerror = () => {
      setConnect(false);
      setMessages((prev) => [
        ...prev,
        {
          message: `Error while connecting to the server. Please check the endpoint and try again.`,
          timestamp: new Date().toString(),
          randomKey: messages.length,
        },
      ]);
      setConnectButtonText(ButtonText.CONNECT);
    };

    ws.onmessage = (event) => {
      setMessages((prev) => [
        ...prev,
        {
          message: `Received: ${event.data}`,
          timestamp: new Date().toString(),
          randomKey: messages.length,
        },
      ]);
    };
  };

  const disconnectWebsocket = () => {
    setConnectButtonText(ButtonText.DISCONNECTING);
    setTimeout(() => {
      socket?.close();
      setConnect(false);
      setConnectButtonText(ButtonText.CONNECT);
      setMessages((prev) => [
        ...prev,
        { message: `Disconnected`, timestamp: new Date().toString() },
      ]);
    }, 1000);
  };

  const clearLogs = () => {
    setMessages([]);
  };

  function stoInt(s: string) {
    return s.split("").reduce((a, b, i) => {
      const r = a + b.charCodeAt(0) * i;
      return r;
    }, 1);
  }

  const colorSet = [
    classes.logColor1,
    classes.logColor2,
    classes.logColor3,
    classes.logColor4,
  ];

  const { current: colorMap } = useRef(new Map<string, string>());
  const getColorCode = (name: string) => {
    if (colorMap.has(name)) {
      return colorMap.get(name) || "";
    }
    const colorCode = colorSet[stoInt(name) % colorSet.length];
    colorMap.set(name, colorCode);
    return colorCode;
  };

  const renderItem = (logEntry: CollectedLogLineWithKey) => (
    <LogLine
      getColor={getColorCode}
      logEntry={logEntry}
      timeZone="UTC"
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      isExpanded={false}
    />
  );

  function getButtonText(connectBtnText: string) {
    switch (connectBtnText) {
      case ButtonText.CONNECTING:
        return (
          <FormattedMessage
            id="modules.testComponent.WebsocketViewer.TopicViewer.connectingButton.label"
            defaultMessage="Connecting..."
          />
        );
      case ButtonText.CONNECT:
        return (
          <FormattedMessage
            id="modules.testComponent.WebsocketViewer.TopicViewer.connectButton.label"
            defaultMessage="Connect"
          />
        );
      case ButtonText.DISCONNECT:
        return (
          <FormattedMessage
            id="modules.testComponent.WebsocketViewer.TopicViewer.disconnectButton.label"
            defaultMessage="Disconnect"
          />
        );
      case ButtonText.DISCONNECTING:
        return (
          <FormattedMessage
            id="modules.testComponent.WebsocketViewer.TopicViewer.disconnectingButton.label"
            defaultMessage="Disconnecting..."
          />
        );
      default:
        return (
          <FormattedMessage
            id="modules.testComponent.WebsocketViewer.TopicViewer.defaultButton.label"
            defaultMessage="Connect"
          />
        );
    }
  }

  const tooltipText = useMemo(
    () =>
      connectButtonText === 'Connect' ? (
        <FormattedMessage
          id="modules.testComponent.WebsocketViewer.TopicViewer.connectButton.tooltip"
          defaultMessage="Connect to the server to start sending messages."
        />
      ) : (
        <FormattedMessage
          id="modules.testComponent.WebsocketViewer.TopicViewer.disconnectButton.tooltip"
          defaultMessage="Enter your message to send."
        />
      ),
    [connectButtonText]
  );

  return (
    <Box className={classes.topicContainer}>
      <Accordion square className={classes.accordion} variant="outlined">
        <AccordionSummary
          className={classes.accordionSummary}
          expandIcon={<ExpandMoreIcon fontSize="small" />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Box className={classes.topicTypeContainer}>
            <Box className={classes.typeChipContainer}>
              <Chip
                className={classes.pubTypeChipContainer}
                label="PUB"
                variant="default"
                size="medium"
                disabled={!publish}
              />
            </Box>
            <Box className={classes.typeChipContainer}>
              <Chip
                className={classes.subTypeChipContainer}
                label="SUB"
                variant="default"
                size="medium"
                disabled={!subscribe}
              />
            </Box>
            <Box className={classes.typeChipContainer}>
              <Typography variant="body2">{topic}</Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.accordinDetails}>
          <Box width="100%">
            {topic && (
              <Suspense
                fallback={
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <CircularProgress size={25} />
                  </Box>
                }
              >
                <Box className={classes.endpointContainer}>
                  <Box className={classes.textInput}>
                    <CopyTextField
                      testId="endpoint-url"
                      textContent={endpoint}
                      className={classes.copyTextField}
                    />
                  </Box>
                  <Button
                    color={connect ? "secondary" : "primary"}
                    variant="contained"
                    onClick={() =>
                      connect ? disconnectWebsocket() : connectWebsocket()
                    }
                    size="small"
                    className={classes.ConnectButton}
                  >
                    {getButtonText(connectButtonText)}
                  </Button>
                </Box>
                <Box>
                  <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    classes={{
                      root: classes.tabRoot,
                      indicator: classes.tabIndicator,
                    }}
                    data-testid="tab-view"
                  >
                    {Object.keys(pathParams).length !== 0 && (
                      <Tab
                        disableRipple={true}
                        disableFocusRipple={true}
                        label="Parameters"
                        classes={{
                          wrapper: classes.tabTitleText,
                          root: classes.tab,
                          selected: classes.selectedTab,
                        }}
                        data-testid="identity-tab"
                      />
                    )}
                    <Tab
                      disableRipple={true}
                      disableFocusRipple={true}
                      label="Payload"
                      classes={{
                        wrapper: classes.tabTitleText,
                        root: classes.tab,
                        selected: classes.selectedTab,
                      }}
                      data-testid="payload-tab"
                    />
                  </Tabs>
                  {Object.keys(pathParams).length !== 0 && (
                    <TabPanel selectedIndex={selectedTab} index={0}>
                      <Box className={classes.parameterContainerWrapper}>
                        <Box className={classes.parameterContainer}>
                          {Object.keys(pathParams).map((param) => (
                            <Box key={param}>
                              <TextField
                                className={classes.paramTextInput}
                                type="text"
                                placeholder={param}
                                size="small"
                                value={pathParams[param]}
                                variant="outlined"
                                onChange={(e) => {
                                  const newParams = {
                                    ...pathParams,
                                    [param]: e.target.value,
                                  };
                                  setPathParams(newParams);
                                }}
                                label={param}
                                error={pathParams[param] == ""}
                                helperText={
                                  pathParams[param] == ""
                                    ? "Enter Parameter Value"
                                    : ""
                                }
                              />
                            </Box>
                          ))}
                        </Box>
                        <Box className={classes.executeButton}>
                          <Button
                            className={classes.containerButton}
                            disabled={connect}
                            variant="outlined"
                            onClick={connectWebsocket}
                            startIcon={<PlayCircleFilledOutlined />}
                            size="small"
                            color="primary"
                          >
                            <Typography variant="body1" align="center">
                              <FormattedMessage
                                id="modules.testComponent.WebsocketViewer.TopicViewer.Execute"
                                defaultMessage="Execute"
                              />
                            </Typography>
                          </Button>
                        </Box>
                      </Box>
                    </TabPanel>
                  )}
                  <TabPanel
                    selectedIndex={selectedTab}
                    index={Object.keys(pathParams).length === 0 ? 0 : 1}
                  >
                    <Box className={classes.payloadContainer}>
                      <PayloadEditor
                        fileContent={input}
                        setFileContent={setInput}
                      />
                    </Box>
                    <Box mt={5}>
                      <Tooltip
                        title={tooltipText}
                        placement="top-start"
                        open={input.length === 0 || !connect}
                      >
                        <Box className={classes.sendIcon}>
                          <Button
                            className={classes.containerButton}
                            disabled={input.length === 0 || !connect}
                            variant="outlined"
                            startIcon={<Send />}
                            onClick={sendMessage}
                            size="small"
                            color="primary"
                          >
                            <Typography variant="body1" align="center">
                              <FormattedMessage
                                id="modules.testComponent.WebsocketViewer.TopicViewer.Send"
                                defaultMessage="Send"
                              />
                            </Typography>
                          </Button>
                        </Box>
                      </Tooltip>
                    </Box>
                  </TabPanel>
                </Box>
                <OutputConsole
                  messages={messages}
                  clearLogs={clearLogs}
                  isDisabled={messages.length < 1}
                />
                <Box>
                  <Box className={classes.outputContainer}>
                    {messages.map((msg, index) =>
                      renderItem({
                        timestamp: msg.timestamp,
                        message: msg.message,
                        randomKey: index,
                        isNewLog: false,
                      })
                    )}
                  </Box>
                </Box>
              </Suspense>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TopicViewer;
